import { FC } from 'react';

import Link from 'next/link';
import { useTranslation } from 'next-i18next/pages';

import { Spin } from 'antd';

import { YandexMap } from '@/components/common';
import { WarehouseHeaderInfo } from '@/components/PersonalAccountWarehouse/HeaderInfo/WarehouseHeaderInfo';
import { IconWithTooltip } from '@/components/PersonalAccountWarehouses/NearbyWarehouseCard/IconWithTooltip';
import { Temperatures } from '@/components/PersonalAccountWarehouses/WarehouseCard/Temperatures';
import { OFFICE_LANDING_URL } from '@/constants/routes';
import { useUserState } from '@/context/user.context';
import { TranslationNamespace } from '@/i18n';
import { SearchedWarehouse } from '@/types/dadata';
import { Storage } from '@/types/storage';
import { BankOutlined } from '@ant-design/icons';

import { CreateApplicationBlock } from './CreateApplicationBlock/CreateApplicationBlock';
import { RegisterBanner } from './RegisterBanner/RegisterBanner';

import styles from './WarehouseModalContent.module.scss';

type Props = {
	isLoadingWarehouseDetails: boolean;
	selectedWarehouse?: SearchedWarehouse;
	warehouseDetails?: Storage;
	hasInspectionRoom: boolean;
	refContainers: boolean;
	loadType: 'manual' | 'automatic';
};

const getWarehouseTemperatures = (warehouse: Storage) => {
	const tempRegimes = warehouse?.temp_regimes;

	if (!tempRegimes) return [];

	const temperatureValues = [
		...Object.keys(tempRegimes.total_volume_temp_regimes || {}),
		...Object.keys(tempRegimes.free_volume_temp_regimes || {}),
		...Object.keys(tempRegimes.refrigerator_free_volume_temp_regimes || {}),
	];

	return Array.from(new Set(temperatureValues))
		.map(Number)
		.filter((temperature) => !Number.isNaN(temperature))
		.sort((firstTemperature, secondTemperature) => secondTemperature - firstTemperature);
};

export const WarehouseModalContent: FC<Props> = ({
	selectedWarehouse,
	warehouseDetails,
	refContainers,
	hasInspectionRoom,
	loadType,
	isLoadingWarehouseDetails,
}) => {
	const { t } = useTranslation(TranslationNamespace.WarehouseSearch);
	const { user } = useUserState();
	const warehouseTemperatures = warehouseDetails
		? getWarehouseTemperatures(warehouseDetails)
		: selectedWarehouse?.temp_regimes || [];
	const temperatureDescription = warehouseTemperatures
		.map((temperature) => `${temperature} °С`)
		.join(', ');

	const [latitude, longitude] = warehouseDetails?.coords?.split(',').map((c) => Number(c)) || [];

	if (isLoadingWarehouseDetails) {
		return <Spin size="large" />;
	}

	return (
		<div className={styles.content}>
			<p className={styles.description}>
				{t('storage-page.warehouse-description-start', {
					title: warehouseDetails?.title || selectedWarehouse?.title,
				})}
				{(warehouseDetails?.location || selectedWarehouse?.location) &&
					t('storage-page.warehouse-address', {
						location: warehouseDetails?.location || selectedWarehouse?.location,
					})}
				{t('storage-page.warehouse-description-end', {
					temperature: temperatureDescription || t('storage-page.not-specified'),
				})}
			</p>

			<div className={styles.contactGrid}>
				<WarehouseHeaderInfo
					warehouse={warehouseDetails}
					customCard={
						<Link
							href={
								warehouseDetails?.digital_office_url
									? warehouseDetails.digital_office_url
									: OFFICE_LANDING_URL
							}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.linkToOffice}
						>
							<div className={styles.officeCard}>
								<div className={styles.contactLabel}>
									{warehouseDetails?.digital_office_url
										? t('storage-page.warehouse-has-digital-office')
										: t('storage-page.represent-this-warehouse')}
								</div>
								<div className={styles.officeValue}>
									{warehouseDetails?.digital_office_url ? (
										<>
											<BankOutlined />
											{t('storage-page.digital-office')}{' '}
										</>
									) : (
										t('storage-page.connect-digital-office')
									)}
								</div>
							</div>
						</Link>
					}
					withSrotageProductsCard={false}
				/>
			</div>

			{warehouseDetails && (
				<>
					<div className={styles.infoGrid}>
						<div>
							<h4 className={styles.sectionTitle}>{t('storage-page.warehouse-information')}</h4>
							<div className={styles.infoList}>
								<div className={styles.infoRow}>
									<span className={styles.label}>{t('storage-page.organization')}</span>
									<div className={styles.value}>
										{warehouseDetails.legal_name || t('storage-page.not-specified-feminine')}
									</div>
								</div>
								<div className={styles.infoRow}>
									<span className={styles.label}>{t('storage-page.tax-id')}</span>
									<div className={styles.value}>
										{warehouseDetails.inn || t('storage-page.not-specified')}
									</div>
								</div>
								<div className={styles.infoRow}>
									<span className={styles.label}>{t('storage-page.temperature-regimes')}</span>
									<div className={styles.temperatureList}>
										{warehouseTemperatures.length > 0 ? (
											<Temperatures visibleCount={100} temperatures={warehouseTemperatures} />
										) : (
											t('storage-page.not-specified-plural')
										)}
									</div>
								</div>
								<div className={styles.infoRow}>
									<span className={styles.label}>{t('storage-page.location')}</span>
									<div className={styles.value}>
										{warehouseDetails.location || t('storage-page.not-specified')}
									</div>
								</div>
								<div className={styles.infoRow}>
									<span className={styles.label}>{t('storage-page.mercury-number')}</span>
									<div className={styles.value}>
										{warehouseDetails.mercury_number || t('storage-page.not-specified')}
									</div>
								</div>
								<div className={styles.infoRow}>
									<span className={styles.label}>{t('storage-page.cerberus-number')}</span>
									<div className={styles.value}>
										{warehouseDetails.cerberus_number || t('storage-page.not-specified')}
									</div>
								</div>
							</div>
						</div>
						<div className={styles.warehouseMap}>
							<YandexMap
								zoom={10}
								center={{ latitude, longitude }}
								markers={[
									{
										id: 'current-warehouse',
										latitude,
										longitude,
										location: warehouseDetails.location,
										title: warehouseDetails.title,
									},
								].map((r) => ({
									id: r.id,
									latitude: r.latitude,
									longitude: r.longitude,
									tooltip: (
										<div className={styles.tooltip} onClick={(e) => e.stopPropagation()}>
											<div className={styles.tooltipTitle}>{r.title}</div>
											<div className={styles.tooltipAddress}>{r.location}</div>
										</div>
									),
								}))}
								mapId="personal-account-warehouse"
							/>
						</div>
					</div>
					<div className={styles.featureBlock}>
						<h4 className={styles.featureTitle}>{t('storage-page.warehouse-features')}</h4>
						<div className={styles.featureList}>
							<div className={styles.featureItem}>
								<div className={styles.iconWrapper}>
									<IconWithTooltip active={hasInspectionRoom} indicator="inspectory" />
								</div>

								{hasInspectionRoom
									? t('storage-page.inspection-room-available')
									: t('storage-page.inspection-room-not-specified')}
							</div>
							<div className={styles.featureItem}>
								<div className={styles.iconWrapper}>
									<IconWithTooltip active={refContainers} indicator="ref" />
								</div>

								{refContainers
									? t('storage-page.ref-containers-connection-available')
									: t('storage-page.ref-containers-connection-not-specified')}
							</div>
							<div className={styles.featureItem}>
								<div className={styles.iconWrapper}>
									<IconWithTooltip active={loadType === 'automatic'} indicator="load" />
								</div>

								{warehouseDetails.loading_unloading_type
									? t('storage-page.mechanized-loading')
									: t('storage-page.manual-loading')}
							</div>
						</div>
					</div>
					{user ? (
						<CreateApplicationBlock
							warehouseId={warehouseDetails.id}
							warehouseTitle={warehouseDetails.title}
							virtual={warehouseDetails.virtual}
							hasProducts={warehouseDetails.has_products}
						/>
					) : (
						<RegisterBanner />
					)}
				</>
			)}
		</div>
	);
};
